import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon, Button, AVATAR_PALETTE } from "../components/ui";
import { DAYS } from "../data/constants";
import { familyService } from "../services/familyService";
import { useFamilyAuth } from "../store/familyAuthStore";
import { childAge, dayList, formatBirthDate, formatPhone, PHONE_DIGITS } from "../utils/format";
import { validateForm } from "../utils/validate";

const ACCOUNT_NAV = [
  { id: "profile", label: "Profile", icon: "user" },
  { id: "children", label: "Children", icon: "users" },
  { id: "security", label: "Security", icon: "lock" },
];

export default function Account() {
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const family = useFamilyAuth((s) => s.family) || {};
  const tab = ACCOUNT_NAV.some((n) => n.id === tabParam) ? tabParam : "profile";
  const [toast, setToast] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2600); };
  return (
    <div className="workspace">
      <aside className="ws-side">
        <p className="eyebrow eyebrow--gap">Your account</p>
        <div className="account-id">
          <span className="account-avatar">
            <Icon name="user" className="ico-18" />
          </span>
          <div>
            <div className="account-name">{family.full_name} family</div>
            <div className="account-email">{family.email}</div>
          </div>
        </div>
        <nav className="acct-nav">
          {ACCOUNT_NAV.map((n) => (
            <button key={n.id} className={"acct-navitem" + (tab === n.id ? " is-active" : "")}
              onClick={() => navigate("/account/" + n.id)}>
              <Icon name={n.icon} className="ico-18" />
              <span className="nav-label">{n.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="ws-main scroll-area">
        <div className="ws-main-inner">
          {tab === "profile" && <ProfilePanel family={family} onSaved={() => showToast("Your changes have been saved.")} />}
          {tab === "children" && <ChildrenPanel />}
          {tab === "security" && <SecurityPanel onSaved={() => showToast("Your password has been updated.")} />}
        </div>
      </div>

      {toast && (
        <div className="toast fade-in">
          <Icon name="checkCircle" className="ico-20 ico-amber" />
          {toast}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, name, onChange, full, type = "text", placeholder, inputMode, required }) {
  return (
    <div className={full ? "col-full" : undefined}>
      <label className="field-label">{label}</label>
      <input
        className="input"
        name={name}
        value={value || ""}
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        onChange={onChange}
        required={required}
      />
    </div>
  );
}

const splitName = (fullName = "") => {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first_name: parts[0] || "", last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
};

const loadCarers = (family) => {
  const split = splitName(family.full_name);
  return {
    primary: {
      first_name: family.primary_first_name || split.first_name,
      last_name: family.primary_last_name || split.last_name,
      relationship: family.primary_relationship || "",
      mobile: family.phone || "",
      email: family.email || "",
    },
    secondary: {
      first_name: family.secondary_first_name || "",
      last_name: family.secondary_last_name || "",
      relationship: family.secondary_relationship || "",
      mobile: family.secondary_phone || "",
      email: family.secondary_email || "",
    },
  };
};

function CarerSection({ title, tag, carer, onChange, prefix = "", requiredFields = false }) {
  const onField = (e) => {
    const field = e.target.name.startsWith(prefix) ? e.target.name.slice(prefix.length) : e.target.name;
    onChange(field, e.target.value);
  };
  return (
    <section className="carer-section">
      <div className="carer-head">
        <h3>{title}</h3>
        <span className="carer-tag">{tag}</span>
      </div>
      <div className="field-grid">
        <Field label="First name" name={`${prefix}first_name`} value={carer.first_name} onChange={onField} required={requiredFields} />
        <Field label="Last name" name={`${prefix}last_name`} value={carer.last_name} onChange={onField} required={requiredFields} />
        <Field label="Relationship to child" name={`${prefix}relationship`} value={carer.relationship} onChange={onField} />
        <Field
          label="Mobile"
          name={`${prefix}mobile`}
          value={carer.mobile}
          onChange={onField}
          type="tel"
          inputMode="numeric"
          placeholder={`${PHONE_DIGITS} digits`}
          required={requiredFields}
        />
        <Field
          label="Email address"
          name={`${prefix}email`}
          value={carer.email}
          onChange={onField}
          type="email"
          full
          required={requiredFields}
        />
      </div>
    </section>
  );
}

function ProfilePanel({ family, onSaved }) {
  const setFamily = useFamilyAuth((s) => s.setFamily);
  const [form, setForm] = useState(() => loadCarers(family));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(loadCarers(family));
    setError("");
  }, [family]);

  const updatePrimary = (name, value) => {
    const next = name === "mobile" ? formatPhone(value) : value;
    setForm((f) => ({ ...f, primary: { ...f.primary, [name]: next } }));
  };

  const updateSecondary = (name, value) => {
    const next = name === "mobile" ? formatPhone(value) : value;
    setForm((f) => ({ ...f, secondary: { ...f.secondary, [name]: next } }));
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (
      !validateForm(e.currentTarget, [
        { name: "primary_first_name", value: form.primary.first_name, label: "First name", required: true },
        { name: "primary_last_name", value: form.primary.last_name, label: "Last name", required: true },
        { name: "primary_email", value: form.primary.email, label: "Email", required: true, type: "email" },
        { name: "primary_mobile", value: form.primary.mobile, label: "Mobile", required: true, type: "auMobile" },
        { name: "secondary_mobile", value: form.secondary.mobile, label: "Secondary mobile", type: "auMobile" },
      ])
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    const payload = {
      primary_first_name: form.primary.first_name.trim(),
      primary_last_name: form.primary.last_name.trim(),
      primary_relationship: form.primary.relationship.trim(),
      email: form.primary.email.trim(),
      phone: form.primary.mobile.trim(),
      secondary_first_name: form.secondary.first_name.trim(),
      secondary_last_name: form.secondary.last_name.trim(),
      secondary_relationship: form.secondary.relationship.trim(),
      secondary_phone: form.secondary.mobile.trim(),
      secondary_email: form.secondary.email.trim(),
    };

    setBusy(true);
    try {
      const res = await familyService.updateProfile(payload);
      if (res?.success) {
        setFamily(res.data?.family || {
          ...family,
          ...payload,
          full_name: [payload.primary_first_name, payload.primary_last_name].join(" "),
        });
        onSaved();
      } else {
        setError(res?.msg || "We couldn't save your changes.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-in">
      <div className="panel-head">
        <p className="eyebrow">Profile</p>
        <h1 className="mt-8">Carers &amp; contact details</h1>
      </div>
      <p className="panel-intro">
        Keep these up to date so your centre can always reach you. Changes are confirmed with your centre team.
      </p>

      {error && <div className="alert-soft">{error}</div>}

      <form id="profile-form" onSubmit={save} noValidate>
        <div className="card carers-card">
          <CarerSection
            title="Primary carer"
            tag="Main contact"
            prefix="primary_"
            requiredFields
            carer={form.primary}
            onChange={updatePrimary}
          />

          <hr className="divider carer-divider" />

          <CarerSection
            title="Secondary carer"
            tag="Additional"
            prefix="secondary_"
            carer={form.secondary}
            onChange={updateSecondary}
          />
        </div>

        <div className="save-bar">
          <Button variant="primary" icon="check" type="submit" disabled={busy}>{busy ? "Saving…" : "Save changes"}</Button>
        </div>
      </form>
    </div>
  );
}

function ChildrenPanel() {
  const [children, setChildren] = useState([]);
  useEffect(() => { familyService.getChildren().then((r) => setChildren(r?.data || [])).catch(() => {}); }, []);

  return (
    <div className="fade-in">
      <div className="panel-head">
        <p className="eyebrow">Children</p>
        <h1 className="mt-8">Your children at Stride</h1>
      </div>
      <p className="panel-intro">
        Booking days and room placements are managed by your centre. To change a booking, use the Forms area.
      </p>
      {children.length === 0 ? (
        <div className="card"><p className="muted m-0">No child profiles yet. Your centre will add these from Xplor.</p></div>
      ) : (
        <div className="children-cards">
          {children.map((c, i) => {
            const color = AVATAR_PALETTE[i % AVATAR_PALETTE.length];
            const onDays = dayList(c.days);
            return (
              <div className="card child-detail" key={c.id}>
                <div className="cd-top">
                  <span className="co-avatar lg" style={{ background: color, color: color === "var(--amber)" ? "var(--brown)" : "#fff" }}>{c.first_name[0]}</span>
                  <div>
                    <h3 className="cd-name">{c.first_name}</h3>
                    <div className="cd-sub">
                      {[childAge(c.dob), c.dob ? `born ${formatBirthDate(c.dob)}` : null].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
                <hr className="divider cd-divider" />
                <div className="cd-rows">
                  <div className="cd-row"><span>Room</span><strong>{c.room || "—"}</strong></div>
                  <div className="cd-row"><span>Group</span><strong>{c.program || "—"}</strong></div>
                  <div className="cd-row"><span>Booked days</span>
                    <span className="day-pills">{DAYS.map((d) => (
                      <span key={d} className={"day-pill" + (onDays.includes(d) ? " on" : "")}>{d[0]}</span>
                    ))}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function SecurityPanel({ onSaved }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (
      !validateForm(e.currentTarget, [
        { name: "current_password", value: cur, label: "Current password", required: true },
        { name: "new_password", value: next, label: "New password", required: true, minLength: 6 },
        { name: "confirm_password", value: confirm, label: "Confirm password", required: true,
          custom: (v) => (v && v !== next ? "Those passwords don't match" : null) },
      ])
    ) {
      if (next.length >= 6 && next !== confirm) setError("Those passwords don't match.");
      else if (next.length > 0 && next.length < 6) setError("New password must be at least 6 characters.");
      else setError("Please fill in all required fields.");
      return;
    }
    setBusy(true);
    try {
      const res = await familyService.changePassword(cur, next);
      if (res?.success) { setCur(""); setNext(""); setConfirm(""); onSaved(); }
      else setError(res?.msg || "We couldn't update your password.");
    } catch (err) {
      setError(err?.response?.data?.msg || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-in">
      <div className="panel-head">
        <p className="eyebrow">Security</p>
        <h1 className="mt-8">Sign-in &amp; security</h1>
      </div>
      {error && <div className="alert-soft">{error}</div>}
      <form id="security-form" onSubmit={save} noValidate>
        <div className="card mt-8">
          <h3 className="mb-20">Change password</h3>
          <div className="field-grid">
            <div className="col-full"><label className="field-label">Current password</label><input className="input" type="password" name="current_password" value={cur} onChange={(e) => setCur(e.target.value)} required /></div>
            <div><label className="field-label">New password</label><input className="input" type="password" name="new_password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} placeholder="At least 6 characters" /></div>
            <div><label className="field-label">Confirm new password</label><input className="input" type="password" name="confirm_password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Re-enter new password" /></div>
          </div>
        </div>
        <div className="save-bar">
          <Button variant="primary" icon="check" type="submit" disabled={busy}>{busy ? "Saving…" : "Update password"}</Button>
        </div>
      </form>     
    </div>
    
  );
}
